import {isLegacyWorker} from "./jobScheduling";
import {getheaders} from './authService';
import {getAllJobsString, assignJobWorker} from './JobService';
import {getAllPropertiesWithDurations} from './PropertyService';
import {getUsers, getUsersFromPayload, getUserbyUsername, getAgencies} from './UserService';
import {getWorkerCatalogs} from './WorkerSettingsService';
import {getWorkOrders} from './PricingService';

export async function read(response) {
    const value = await response.json().catch(() => null);
    if (!response.ok) throw new Error(value?.message || value?.detail || `Request failed (${response.status})`);
    if (value == null) throw new Error('Server returned an empty or invalid JSON response');
    return value;
}
export function ruleRequest(path, value) {
    return fetch(`/api/${path}`, {method: value === undefined ? 'GET' : 'PUT', headers: getheaders(),
        ...(value === undefined ? {} : {body: JSON.stringify(value)})}).then(read);
}
export const loadSettings = region => ruleRequest(`scheduler/auto-assign/settings?region=${encodeURIComponent(region)}`);
export const saveSettings = (region, value) => ruleRequest(`scheduler/auto-assign/settings?region=${encodeURIComponent(region)}`, value);
export const loadPropertyRule = id => ruleRequest(`properties/${id}/auto-assign-rule`);
export const savePropertyRule = (id, value) => ruleRequest(`properties/${id}/auto-assign-rule`, value);
export const loadPropertyRules = region => ruleRequest(`scheduler/auto-assign/property-rules?region=${encodeURIComponent(region)}`);
async function mapLimited(values, mapper) {
    const results = new Array(values.length);
    let next = 0;
    await Promise.all(Array.from({length: Math.min(6, values.length)}, async () => {
        while (next < values.length) { const index = next++; results[index] = await mapper(values[index]); }
    }));
    return results;
}
export async function loadSnapshot(date, region) {
    const [jobs, properties, directory, catalogs, orders, agencies, settings, rules] = await Promise.all([
        getAllJobsString(date, date).then(read), getAllPropertiesWithDurations().then(read),
        getUsers({roles: ['WORKER']}).then(read), getWorkerCatalogs(), getWorkOrders(date, date).then(read),
        getAgencies().then(read), loadSettings(region), loadPropertyRules(region),
    ]);
    const workers = await mapLimited(getUsersFromPayload(directory).filter(user => user.region === region && user.account_state === 'ACTIVE'
        && !isLegacyWorker(user.cos_customer_id)), async user => ({...user, profile: (await getUserbyUsername(encodeURIComponent(user.username)).then(read)).worker_profile}));
    return {date, region, jobs, properties, workers, catalogs, orders, agencies, settings, rules};
}
export function stableFingerprint(value) {
    function canonical(item) {
        if (Array.isArray(item)) return item.map(canonical).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
        if (item && typeof item === 'object') return Object.fromEntries(Object.keys(item).sort().map(key => [key, canonical(item[key])]));
        return item;
    }
    return JSON.stringify(canonical(value));
}
export function fingerprint(value) {
    // Ignore unrelated presence metadata, but compare all actual scheduling inputs.
    return stableFingerprint({date: value.date, region: value.region, jobs: value.jobs, properties: value.properties,
        workers: value.workers.map(w => ({id: w.cos_customer_id, region: w.region, state: w.account_state, profile: w.profile})),
        catalogs: value.catalogs, orders: value.orders, agencies: value.agencies, settings: value.settings, rules: value.rules});
}
export async function submitAssignments(rows, onResult, isCancelled = () => false) {
    for (const row of rows) {
        if (isCancelled()) return {stopped: true};
        try {
            const job = await assignJobWorker(row.id, row.workerId).then(read);
            if (!job || String(job.cos_job_id) !== String(row.id) || String(job.cos_job_worker) !== String(row.workerId)) throw new Error('Assignment response was not confirmed. Reload actual assignments.');
            onResult(row.id, {ok: true, job});
        } catch (error) {
            onResult(row.id, {ok: false, message: error.message});
            return {stopped: true, error: error.message};
        }
    }
    return {stopped: false};
}
